import { BaseMessageKeys, ErrorKeyType } from "../models/errors.models";
import { BASE_MESSAGES } from "./base-error-messages.constants";

/**
 * Naming convention for error message keys.
 * Used to dynamically generate prefixed keys for the `ERROR_MESSAGES` object.
 */
export const ERROR_MESSAGE_NAME_CONVENTION = 'ERROR_MESSAGE';

/**
 * Naming convention for error key constants.
 * Used to dynamically generate prefixed keys for the `ERROR_KEYS` object.
 */
export const ERROR_KEY_NAME_CONVENTION = 'ERROR';

/**
 * Generates `ERROR_MESSAGES` by prefixing each key in `BASE_MESSAGES` with `ERROR_MESSAGE_NAME_CONVENTION`.
 * Dynamically transforms each base message key to follow the naming convention.
 *
 * @returns A record where each key is prefixed with `ERROR_MESSAGE_NAME_CONVENTION` and mapped to its corresponding value from `BASE_MESSAGES`.
 */
export const ERROR_MESSAGES: Record<`${typeof ERROR_MESSAGE_NAME_CONVENTION}_${BaseMessageKeys}`, string> =
  Object.fromEntries(
    Object.entries(BASE_MESSAGES).map(([key, value]) => [
      `${ERROR_MESSAGE_NAME_CONVENTION}_${key}`,
      value,
    ])
  ) as Record<`${typeof ERROR_MESSAGE_NAME_CONVENTION}_${BaseMessageKeys}`, string>;

/**
 * Generates `ERROR_KEYS` by prefixing each key in `BASE_MESSAGES` with `ERROR_KEY_NAME_CONVENTION`.
 * Dynamically constructs the value for each key using the pattern `ERROR_<BASE_MESSAGE_KEY>`.
 *
 * @returns A record where each key is prefixed with `ERROR_KEY_NAME_CONVENTION` and mapped to a dynamically constructed string.
 */
export const ERROR_KEYS: Record<`${typeof ERROR_KEY_NAME_CONVENTION}_${BaseMessageKeys}`, string> =
  Object.fromEntries(
    Object.entries(BASE_MESSAGES).map(([key]) => [
      `${ERROR_KEY_NAME_CONVENTION}_${key}`,
      `ERROR_${key}`,
    ])
  ) as Record<`${typeof ERROR_KEY_NAME_CONVENTION}_${BaseMessageKeys}`, string>;

/**
 * Retrieves the value associated with a given `ERROR_KEY`.
 *
 * @param key - A key of type `ErrorKeyType`, ensuring it corresponds to a valid entry in `ERROR_KEYS`.
 * @returns The value associated with the provided `key` from the `ERROR_KEYS` object.
 */
export const ERROR_KEY = (key: ErrorKeyType): string => {
  return ERROR_KEYS[key];
};
